import { z } from "zod/v4";
import { clubMemberSchema } from "./club-member";
import {
	communityBadgeTierSchema,
	communityTypeSchema,
	joinPolicySchema,
	moderationStatusSchema,
	skillLevelSchema,
	subscriptionTierSchema,
} from "./shared";

export const clubDeletionSchema = z.discriminatedUnion("status", [
	z.object({ status: z.literal("live") }),
	z.object({
		days_remaining: z.number().int().min(1),
		hidden_at: z.coerce.date(),
		purges_at: z.coerce.date(),
		status: z.literal("pending"),
	}),
	z.object({
		hidden_at: z.coerce.date(),
		status: z.literal("retained"),
	}),
]);

export const clubSchema = z.object({
	avatar_url: z.string().nullable().optional(),
	badge_tier: communityBadgeTierSchema.optional(),
	boost_priority: z.number().int().nullable().optional(),
	boosted_until: z.coerce.date().nullable().optional(),
	contact_email: z.string().nullable().optional(),
	created_at: z.coerce.date(),
	creator_id: z.string().uuid(),
	deletion: clubDeletionSchema,
	description: z.string().nullable().optional(),
	id: z.string().uuid(),
	is_boosted: z.boolean().optional(),
	is_member: z.boolean(),
	is_on_trial: z.boolean().optional(),
	is_public: z.boolean(),
	is_verified: z.boolean(),
	location_name: z.string().nullable().optional(),
	logo_url: z.string().nullable().optional(),
	max_members: z.number().int().nullable().optional(),
	member_count: z.number().int(),
	member_limit: z.number().int().nullable().optional(),
	moderation_status: moderationStatusSchema,
	name: z.string(),
	// Set on a training group that a club runs; absent on a user-run one.
	parent_club_id: z.string().uuid().nullable().optional(),
	parent_club_name: z.string().nullable().optional(),
	setup_completed: z.boolean().optional(),
	subscription_tier: subscriptionTierSchema.optional(),
	trial_ends_at: z.coerce.date().nullable().optional(),
	trial_tier: subscriptionTierSchema.nullable().optional(),
	trial_used: z.boolean().optional(),
	type: communityTypeSchema,
	website_url: z.string().nullable().optional(),
});

export const createClubSchema = z.object({
	avatar_url: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	is_public: z.boolean().optional(),
	max_members: z.number().int().min(2).nullable().optional(),
	name: z.string(),
	type: communityTypeSchema.optional(),
});

export const updateClubSchema = z.object({
	avatar_url: z.string().nullable().optional(),
	banner_url: z.string().nullable().optional(),
	contact_email: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	is_public: z.boolean().optional(),
	is_verified: z.boolean().optional(),
	logo_url: z.string().nullable().optional(),
	max_members: z.number().int().min(2).nullable().optional(),
	name: z.string().min(1).optional(),
	type: communityTypeSchema.optional(),
	website_url: z.string().nullable().optional(),
});

// Admin-only: set a club's discover boost via PUT /admin/clubs/:id/boost.
export const updateBoostSchema = z.object({
	boost_priority: z.number().int().min(0).max(100),
	boosted_until: z.date().nullable(),
});

// Admin-only: change a club's subscription tier via PUT /admin/clubs/:id/subscription.
export const updateSubscriptionSchema = z.object({
	subscription_tier: subscriptionTierSchema,
});

export const capabilitiesSchema = z.object({
	can_delete: z.boolean(),
	can_edit: z.boolean(),
	can_invite: z.boolean(),
	can_leave: z.boolean(),
	// Owner/admin, or a member granted the manage_finances permission.
	can_manage_finances: z.boolean().default(false),
	can_manage_members: z.boolean(),
	join_policy: joinPolicySchema,
});

export const clubDetailSchema = z.object({
	avatar_url: z.string().nullable().optional(),
	badge_tier: communityBadgeTierSchema.optional(),
	banner_url: z.string().nullable().optional(),
	capabilities: capabilitiesSchema,
	contact_email: z.string().nullable().optional(),
	created_at: z.coerce.date(),
	creator_id: z.string().uuid(),
	deletion: clubDeletionSchema,
	description: z.string().nullable().optional(),
	id: z.string().uuid(),
	is_member: z.boolean(),
	is_on_trial: z.boolean().optional(),
	is_public: z.boolean(),
	is_verified: z.boolean(),
	location_name: z.string().nullable().optional(),
	logo_url: z.string().nullable().optional(),
	max_members: z.number().int().nullable().optional(),
	member_count: z.number().int(),
	member_limit: z.number().int().nullable().optional(),
	members: z.array(clubMemberSchema),
	moderation_status: moderationStatusSchema,
	name: z.string(),
	// Set on a training group that a club runs; absent on a user-run one.
	parent_club_id: z.string().uuid().nullable().optional(),
	parent_club_name: z.string().nullable().optional(),
	setup_completed: z.boolean().optional(),
	subscription_tier: subscriptionTierSchema,
	trial_ends_at: z.coerce.date().nullable().optional(),
	trial_tier: subscriptionTierSchema.nullable().optional(),
	trial_used: z.boolean().optional(),
	type: communityTypeSchema,
	website_url: z.string().nullable().optional(),
});

const standardCommunityCreationSchema = clubDetailSchema.extend({
	type: z.enum(["club", "group", "team"]),
});

const squadCreationMemberSchema = z.object({
	avatar_url: z.string().nullable().optional(),
	display_name: z.string(),
	role: z.enum(["organizer", "trainer", "member"]),
	roster_type: z.enum(["core", "reserve"]),
	skill_level: skillLevelSchema,
	user_id: z.string().uuid(),
});

const squadCreationSessionSchema = z.object({
	attending_count: z.number().int(),
	date: z.coerce.date(),
	end_time: z.string(),
	going_count: z.number().int(),
	id: z.string().uuid(),
	start_time: z.string(),
});

const squadCreationCostConfigSchema = z.object({
	court_fee_cents: z.number().int(),
	currency: z.string(),
	trainer_fee_cents: z.number().int(),
});

const squadCreationDetailsSchema = z.object({
	invite_token: z.string().nullable().optional(),
	location_id: z.string().uuid().nullable().optional(),
	location_image_url: z.string().nullable().optional(),
	location_name: z.string().nullable().optional(),
});

const squadCommunityCreationSchema = clubDetailSchema.omit({ members: true, type: true }).extend({
	cost_config: squadCreationCostConfigSchema,
	location_id: z.string().uuid().nullable().optional(),
	location_image_url: z.string().nullable().optional(),
	location_name: z.string().nullable().optional(),
	members: z.array(squadCreationMemberSchema),
	my_role: z.enum(["organizer", "trainer", "member"]).nullable().optional(),
	next_session: squadCreationSessionSchema.nullable().optional(),
	rsvp_cutoff_hours: z.number().int(),
	searchable: z.boolean(),
	squad_details: squadCreationDetailsSchema,
	type: z.literal("squad"),
});

export const communityCreationSchema = z.discriminatedUnion("type", [
	standardCommunityCreationSchema,
	squadCommunityCreationSchema,
]);

// Body for POST /clubs/:id/trial — start a self-serve trial of a paid tier.
export const startTrialSchema = z.object({
	tier: z.enum(["club", "club_pro"]),
});

export const discoverableClubSchema = z.object({
	avatar_url: z.string().nullable().optional(),
	banner_url: z.string().nullable().optional(),
	boost_priority: z.number().int().nullable().optional(),
	boosted_until: z.coerce.date().nullable().optional(),
	created_at: z.coerce.date(),
	creator_id: z.string().uuid(),
	description: z.string().nullable().optional(),
	id: z.string().uuid(),
	is_public: z.boolean(),
	is_verified: z.boolean(),
	logo_url: z.string().nullable().optional(),
	max_members: z.number().int().nullable().optional(),
	moderation_status: moderationStatusSchema,
	name: z.string(),
	rank_score: z.number().nullable().optional(),
	subscription_tier: subscriptionTierSchema,
	type: communityTypeSchema,
});
